import {
  BaseEdge, EdgeLabelRenderer, Handle, MarkerType, Position, ReactFlow, getSmoothStepPath, getStraightPath,
  type Edge, type EdgeProps, type Node, type NodeProps, type OnNodesChange,
} from "@xyflow/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import type { ItineraryDay, ItineraryNode, TimeSlot } from "../types";
import "@xyflow/react/dist/style.css";
import "./itineraryFlow.css";

type ContentNode = Node<{ content: ReactNode; compact?: boolean }, "point" | "slot">;
type RouteEdge = Edge<{ content: ReactNode; centerY?: number; labelX?: number; sourceLaneOffset?: number; targetLaneOffset?: number; vertical: boolean; walking?: boolean; onMeasure: (id: string, height: number) => void }, "route">;
type FlowRoute = {
  id: string;
  source: string;
  target: string;
  content: ReactNode;
  color: string;
  walking: boolean;
  continuation?: { title: string; dayNumber: number };
};

/** 点位业务操作复用现有卡片，端点坐标交给 React Flow 跟踪。 */
function FlowPoint({ data }: NodeProps<ContentNode>) {
  return <div className="itinerary-flow-point nodrag nopan">
    <Handle id="left" type="target" position={Position.Left} style={{ top: 38 }} />
    <Handle id="top" type="target" position={Position.Top} />
    <ol className="itinerary-flow-point-list">{data.content}</ol>
    <Handle id="right" type="source" position={Position.Right} style={{ top: 38 }} />
    <Handle id="bottom" type="source" position={Position.Bottom} />
  </div>;
}

/** 时段是不可拖动的背景分区，保留空时段添加与拖放入口。 */
function FlowSlot({ data }: NodeProps<ContentNode>) {
  return <>{data.content}</>;
}

/** 使用库的圆角折线路径和 HTML 边标签，交通卡与线路端点同步。 */
function FlowRouteEdge(props: EdgeProps<RouteEdge>) {
  const label = useRef<HTMLDivElement>(null);
  const onMeasure = props.data?.onMeasure;
  useLayoutEffect(() => {
    const element = label.current;
    if (!element || !onMeasure) return;
    const measure = () => onMeasure(props.id, Math.ceil(element.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [props.id, onMeasure]);
  const vertical = props.data?.vertical;
  const sourceLaneOffset = props.data?.sourceLaneOffset ?? 24;
  const targetLaneOffset = props.data?.targetLaneOffset ?? 24;
  const laneX = props.targetX - targetLaneOffset;
  const laneY = props.targetY - targetLaneOffset;
  const exitX = props.sourceX + sourceLaneOffset;
  const exitY = props.sourceY + 16;
  const centerY = props.data?.centerY ?? (props.sourceY + props.targetY) / 2;
  const direction = laneX >= exitX ? 1 : -1;
  const turn = Math.min(16, Math.abs(laneX - exitX) / 2);
  const [exitPath] = getSmoothStepPath({
    sourceX: props.sourceX, sourceY: props.sourceY, sourcePosition: Position.Right,
    targetX: exitX, targetY: exitY, targetPosition: Position.Top,
    borderRadius: 12, offset: 0,
  });
  const [path, defaultLabelX, defaultLabelY] = getSmoothStepPath({
    sourceX: props.sourceX, sourceY: props.sourceY, sourcePosition: props.sourcePosition,
    targetX: props.targetX, targetY: props.targetY, targetPosition: props.targetPosition,
    borderRadius: 12, offset: 24,
  });
  const [descentPath] = getSmoothStepPath({
    sourceX: exitX, sourceY: exitY, sourcePosition: Position.Bottom,
    targetX: exitX + direction * turn, targetY: centerY, targetPosition: direction > 0 ? Position.Left : Position.Right,
    borderRadius: 12, offset: 0,
  });
  const [bridgePath] = getStraightPath({
    sourceX: exitX + direction * turn, sourceY: centerY,
    targetX: laneX - direction * turn, targetY: centerY,
  });
  const [approachPath] = getSmoothStepPath({
    sourceX: laneX - direction * turn, sourceY: centerY, sourcePosition: direction > 0 ? Position.Right : Position.Left,
    targetX: laneX, targetY: laneY, targetPosition: laneY > centerY ? Position.Top : Position.Bottom,
    borderRadius: 12, offset: 0,
  });
  const [arrivalPath] = getSmoothStepPath({
    sourceX: laneX, sourceY: laneY, sourcePosition: Position.Bottom,
    targetX: props.targetX, targetY: props.targetY, targetPosition: Position.Left,
    borderRadius: 12, offset: 0,
  });
  const labelX = props.data?.labelX ?? defaultLabelX;
  const labelY = vertical ? centerY : defaultLabelY;
  return <>
    {vertical ? <BaseEdge id={`${props.id}-exit`} path={exitPath} style={props.style} interactionWidth={0} /> : null}
    {vertical ? <BaseEdge id={`${props.id}-descent`} path={descentPath} style={props.style} interactionWidth={0} /> : null}
    <BaseEdge id={props.id} path={vertical ? bridgePath : path} markerEnd={vertical ? undefined : props.markerEnd} style={props.style} interactionWidth={0} />
    {vertical ? <BaseEdge id={`${props.id}-approach`} path={approachPath} style={props.style} interactionWidth={0} /> : null}
    {vertical ? <BaseEdge id={`${props.id}-arrival`} path={arrivalPath} markerEnd={props.markerEnd} style={props.style} interactionWidth={0} /> : null}
    <EdgeLabelRenderer>
      <div ref={label} className={`itinerary-flow-route-label nodrag nopan${props.data?.walking ? " is-walking" : ""}`} style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>
        {props.data?.content}
      </div>
    </EdgeLabelRenderer>
  </>;
}

const nodeTypes = { point: FlowPoint, slot: FlowSlot };
const edgeTypes = { route: FlowRouteEdge };
const slots: TimeSlot[] = ["morning", "afternoon", "evening"];

/** 普通与专业日程共用的展示投影；画布坐标不参与业务草稿或持久化。 */
export function ItineraryFlowCanvas({ day, points, columns, routes, renderPoint, renderSlot }: {
  day: ItineraryDay;
  points: ItineraryNode[];
  columns: Map<string, number>;
  routes: FlowRoute[];
  renderPoint: (point: ItineraryNode) => ReactNode;
  renderSlot: (slot: TimeSlot) => ReactNode;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const [touchLayout, setTouchLayout] = useState(false);
  const [measurements, setMeasurements] = useState(new Map<string, { width: number; height: number }>());
  const [routeHeights, setRouteHeights] = useState(new Map<string, number>());
  const measureRoute = useCallback((id: string, height: number) => {
    setRouteHeights((current) => current.get(id) === height ? current : new Map(current).set(id, height));
  }, []);
  const onNodesChange: OnNodesChange<ContentNode> = useCallback((changes) => {
    const dimensions = changes.filter((change) => change.type === "dimensions");
    if (!dimensions.length) return;
    setMeasurements((current) => {
      const next = new Map(current);
      dimensions.forEach((change) => { if (change.dimensions) next.set(change.id, change.dimensions); });
      return dimensions.some(({ id }) => current.get(id)?.width !== next.get(id)?.width
        || current.get(id)?.height !== next.get(id)?.height) ? next : current;
    });
  }, []);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px), (pointer: coarse)");
    const update = () => setTouchLayout(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!container.current || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  const narrow = touchLayout || width < 600;
  const pointWidth = Math.min(220, Math.max(180, width - 32));
  const pointHeight = narrow ? 222 : 100;
  const columnCount = Math.max(1, Math.floor((width - 64 + 252) / (pointWidth + 252)));
  const columnGap = 252;
  const nodes: ContentNode[] = [];
  const positions = new Map<string, { x: number; y: number; slot: TimeSlot; band: number; bandBottom: number }>();
  const slotBottoms = new Map<TimeSlot, number>();
  const shownSlots = day.isFullDay ? ["morning" as const] : slots;
  const trackOrder = new Map<string, number>();
  day.parallelTracks?.tracks.forEach((track, index) => {
    track.nodeIds.forEach((id) => { if (!trackOrder.has(id)) trackOrder.set(id, index); });
  });

  // 同端点的不同交通事实保留各自操作，放在同一连接上的标签组内。
  const connections = new Map<string, FlowRoute[]>();
  routes.forEach((route) => {
    const key = `${route.source}:${route.target}`;
    connections.set(key, [...connections.get(key) ?? [], route]);
  });
  // 边标签不参与 React Flow 节点测量；整组实测高度同时约束行距与折返通道，避免提示或多方案撑高后互相遮挡。
  const routeHeight = Math.max(80, ...[...connections.values()].map((items) => routeHeights.get(items[0].id) ?? items.length * 112 + (items.length - 1) * 8));
  const routeLaneHeight = routeHeight + 16;
  const pointTopInset = Math.max(0, routeHeight / 2 - 38);
  let top = 0;
  shownSlots.forEach((slot) => {
    const slotPoints = points.filter((point) => day.isFullDay || point.timeSlot === slot);
    const stages = new Map<number, Array<{ id: string; content: ReactNode }>>();
    slotPoints.forEach((point, index) => {
      const column = columns.get(point.id) ?? index + 1;
      stages.set(column, [...stages.get(column) ?? [], { id: point.id, content: renderPoint(point) }]);
    });
    const continuationIds = new Set<string>();
    routes.forEach((route) => {
      const source = slotPoints.find((point) => point.id === route.source);
      if (!source || !route.continuation || continuationIds.has(route.target)) return;
      continuationIds.add(route.target);
      const column = Math.max(0, ...stages.keys()) + 1;
      stages.set(column, [{ id: route.target, content: <li className="itinerary-flow-continuation">
        <span>衔接第 {route.continuation.dayNumber} 天</span><strong>{route.continuation.title}</strong>
      </li> }]);
    });
    const sortedStages = [...stages].sort(([left], [right]) => left - right);
    sortedStages.forEach(([, items]) => items.sort((left, right) => (trackOrder.get(left.id) ?? 0) - (trackOrder.get(right.id) ?? 0)));
    const slotIds = new Set(sortedStages.flatMap(([, items]) => items.map((item) => item.id)));
    let bandTop = top + Math.max(48, pointTopInset + 40);
    let lastBandBottom = bandTop;
    const pointNodes: ContentNode[] = [];
    for (let start = 0; start < sortedStages.length; start += columnCount) {
      const band = sortedStages.slice(start, start + columnCount);
      const laneCount = Math.max(...band.map(([, items]) => items.length));
      const laneHeight = Math.max(pointHeight + 56, routeLaneHeight);
      const bandBottom = bandTop + (laneCount - 1) * laneHeight + Math.max(pointHeight, 38 + routeHeight / 2);
      lastBandBottom = bandBottom;
      band.forEach(([, items], index) => {
        items.forEach((item, lane) => {
          const x = columnCount === 1 ? (width - pointWidth) / 2 : 32 + index * (pointWidth + columnGap);
          const y = bandTop + (items.length === 1 ? (laneCount - 1) / 2 : lane) * laneHeight;
          positions.set(item.id, { x, y, slot, band: start, bandBottom });
          pointNodes.push({ id: item.id, type: "point", position: { x, y }, width: pointWidth, height: pointHeight, data: { content: item.content }, draggable: false, selectable: false, focusable: false });
        });
      });
      const bandIds = new Set(band.flatMap(([, items]) => items.map((item) => item.id)));
      const bandColumns = new Map(band.flatMap(([, items], index) => items.map((item) => [item.id, index] as const)));
      const skipsColumn = (route: FlowRoute) => bandIds.has(route.target) && Math.abs(bandColumns.get(route.source)! - bandColumns.get(route.target)!) > 1;
      const outgoingCount = [...connections.values()].filter(([route]) => bandIds.has(route.source) && slotIds.has(route.target) && (!bandIds.has(route.target) || skipsColumn(route))).length;
      // 较短分支跨过中间阶段汇合时也走下方通道，避免穿过其他专业的点位。
      if ([...connections.values()].some(([route]) => bandIds.has(route.source) && skipsColumn(route))) lastBandBottom = bandBottom + outgoingCount * routeLaneHeight + 16;
      bandTop = bandBottom + Math.max(40, outgoingCount * routeLaneHeight + 16) + pointTopInset;
    }
    const height = sortedStages.length ? lastBandBottom + 20 - top : 102;
    nodes.push({ id: `slot-${day.id}-${slot}`, type: "slot", position: { x: 0, y: top }, width, height, style: { width, height }, data: { content: renderSlot(slot) }, zIndex: -1, draggable: false, selectable: false, focusable: false }, ...pointNodes);
    slotBottoms.set(slot, top + height);
    const crossCount = [...connections.values()].filter(([route]) => {
      const source = points.find((point) => point.id === route.source);
      const target = points.find((point) => point.id === route.target);
      return !day.isFullDay && source?.timeSlot === slot && target && target.timeSlot !== slot;
    }).length;
    top += height + (crossCount ? crossCount * routeLaneHeight + 16 : 18);
  });

  const crossIndexes = new Map<TimeSlot, number>();
  const bandIndexes = new Map<string, number>();
  const positionedConnections = [...connections.values()].sort(([left], [right]) => {
    const sourceOrder = (positions.get(right.source)?.y ?? 0) - (positions.get(left.source)?.y ?? 0);
    return sourceOrder || (positions.get(left.target)?.y ?? 0) - (positions.get(right.target)?.y ?? 0);
  }).flatMap((items) => {
    const route = items[0];
    const source = positions.get(route.source);
    const target = positions.get(route.target);
    if (!source || !target) return [];
    const crossSlot = source.slot !== target.slot;
    const skipsColumn = Math.abs(target.x - source.x) > pointWidth + columnGap + 1;
    const vertical = crossSlot || source.band !== target.band || columnCount === 1 || skipsColumn;
    const crossIndex = crossIndexes.get(source.slot) ?? 0;
    if (crossSlot) crossIndexes.set(source.slot, crossIndex + 1);
    const bandKey = `${source.slot}:${source.band}`;
    const bandIndex = bandIndexes.get(bandKey) ?? 0;
    if (vertical && !crossSlot) bandIndexes.set(bandKey, bandIndex + 1);
    const centerY = crossSlot ? (slotBottoms.get(source.slot) ?? source.y + pointHeight) + 16 + routeHeight / 2 + crossIndex * routeLaneHeight
      : vertical ? source.bandBottom + 16 + routeHeight / 2 + bandIndex * routeLaneHeight : undefined;
    return [{ items, route, source, target, vertical, centerY }];
  });
  type SideLane = { id: string; endpointY: number; centerY: number };
  const sourceLaneGroups = new Map<string, SideLane[]>();
  const targetLaneGroups = new Map<string, SideLane[]>();
  positionedConnections.forEach(({ route, source, target, vertical, centerY }) => {
    if (!vertical) return;
    const sourceKey = `${source.slot}:${source.band}:${source.x}`;
    const targetKey = `${target.slot}:${target.band}:${target.x}`;
    sourceLaneGroups.set(sourceKey, [...sourceLaneGroups.get(sourceKey) ?? [], { id: route.id, endpointY: source.y, centerY: centerY! }]);
    targetLaneGroups.set(targetKey, [...targetLaneGroups.get(targetKey) ?? [], { id: route.id, endpointY: target.y, centerY: centerY! }]);
  });
  sourceLaneGroups.forEach((lanes) => lanes.sort((left, right) => left.endpointY - right.endpointY || right.centerY - left.centerY));
  targetLaneGroups.forEach((lanes) => lanes.sort((left, right) => right.endpointY - left.endpointY || left.centerY - right.centerY));
  // 同列点位的不同连接使用独立侧通道，不能在到达真实汇合点前重叠成一条线。
  const laneOffset = (lanes: SideLane[], id: string, clearance: number) => {
    const outer = Math.min(28, Math.max(8, clearance - 4));
    const inner = Math.min(12, outer / 2);
    return lanes.length > 1 ? outer - lanes.findIndex((lane) => lane.id === id) * (outer - inner) / (lanes.length - 1) : Math.min(24, outer);
  };
  const edges: RouteEdge[] = positionedConnections.map(({ items, route, source, target, vertical, centerY }) => {
    const sourceLaneOffset = vertical ? laneOffset(sourceLaneGroups.get(`${source.slot}:${source.band}:${source.x}`)!, route.id, width - source.x - pointWidth) : undefined;
    const targetLaneOffset = vertical ? laneOffset(targetLaneGroups.get(`${target.slot}:${target.band}:${target.x}`)!, route.id, target.x) : undefined;
    const labelX = vertical ? Math.max(119, Math.min(width - 119, (source.x + pointWidth + target.x) / 2)) : undefined;
    const color = items.length === 1 ? route.color : "#94a3b8";
    return { id: route.id, type: "route", source: route.source, target: route.target,
      sourceHandle: "right", targetHandle: "left",
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      style: { stroke: color, strokeWidth: 1.5, strokeDasharray: items.every((item) => item.walking) ? "5 4" : undefined },
      data: { centerY, labelX, sourceLaneOffset, targetLaneOffset, vertical, walking: route.walking, onMeasure: measureRoute, content: <>{items.map((item) => <div key={item.id}>{item.content}</div>)}</> },
      selectable: false, focusable: false,
    };
  });

  return <div ref={container} className={`itinerary-flow-canvas${narrow ? " is-compact" : ""}`} style={{ height: Math.max(140, top) }} aria-label="行程节点与交通连线">
    {/* 保留库回报的测量值；拖动高亮重绘不能清空全部节点的连接端点。 */}
    <ReactFlow nodes={nodes.map((node) => ({ ...node, measured: measurements.get(node.id) }))}
      onNodesChange={onNodesChange} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
      nodesDraggable={false} nodesConnectable={false} edgesReconnectable={false} elementsSelectable={false}
      nodesFocusable={false} edgesFocusable={false} deleteKeyCode={null}
      panOnDrag={false} panOnScroll={false} zoomOnScroll={false} zoomOnPinch={false} zoomOnDoubleClick={false}
      preventScrolling={false} minZoom={1} maxZoom={1} defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      attributionPosition="bottom-right"
    />
  </div>;
}
